import type { AccordionProps } from '@/components/landing-page/components/accordion/accordion.types';
import { useRef, useState } from 'react';
import { Triangle, ChevronDown } from 'lucide-react';
import { useWindowSize } from 'usehooks-ts';
import HeightAnimation from '@/animations/height';
import RotateOpenAnimation from '@/animations/rotate-open';
import { defaults } from '@/components/landing-page/components/accordion';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { SliderSlice } from '@/redux/slider';
import Resolver from '@/components/landing-page/utils/resolver';
import { cn } from '@/utils/helper';
import v1 from '@/components/landing-page/components/accordion/accordion-v1.module.css';
import v2 from '@/components/landing-page/components/accordion/accordion-v2.module.css';

const Accordion = (props: AccordionProps) => {
  const { title = defaults.title, blocks = defaults.blocks, duration = defaults.duration } = props;
  const { width } = useWindowSize();
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const mappedAnswers = useAppSelector(SliderSlice.selectors.mappedAnswers);
  const [current, setCurrent] = useState(-1);
  const refs = useRef<HTMLDivElement[]>([]);
  const resolver = new Resolver(mappedAnswers, width);

  const updateCurrentAccordion = (i: number): void => {
    setCurrent(current === i ? -1 : i);
  };

  return (
    <div className={cn(v1.Accordion, v2.Accordion)}>
      {title && <h6 className={cn(v1.Title, v2.Title)} dangerouslySetInnerHTML={{ __html: title }} />}
      {blocks
        .filter((block) => !block.condition || resolver.check(block.condition))
        .map((block, i) => (
          <div key={i} className={cn(v1.Block, v2.Block)}>
            <div className={cn(v1.BlockTitle, v2.BlockTitle)} onClick={() => updateCurrentAccordion(i)}>
              <RotateOpenAnimation
                on={current === i}
                direction={cssVersion === 1 ? 'right-down' : 'up-down'}
                duration={duration}
                className={cn(v1.Rotate, v2.Rotate)}
              >
                {cssVersion === 1 ? (
                  <Triangle className={cn(v1.Arrow, v2.Arrow)} />
                ) : (
                  <ChevronDown className={cn(v1.Arrow, v2.Arrow)} />
                )}
              </RotateOpenAnimation>
              <h6
                onKeyDown={() => updateCurrentAccordion(i)}
                className={cn(v1.Text, v2.Text)}
                dangerouslySetInnerHTML={{ __html: block.title }}
              />
            </div>
            <HeightAnimation on={current === i} duration={duration} className={cn(v1.Body, v2.Body)}>
              <div
                ref={(el: HTMLDivElement) => (refs.current[i] = el)}
                className={cn(v1.Content, v2.Content)}
                dangerouslySetInnerHTML={{ __html: block.content }}
              />
            </HeightAnimation>
          </div>
        ))}
    </div>
  );
};

export default Accordion;
